import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Lock, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

/**
 * Dedicated platform-admin login. Completely decoupled from any tenant.
 * After successful credential login → navigate to /admin/2fa (which triggers
 * the OTP flow). Only super_admin accounts are accepted; regular tenant
 * users get redirected back to /login with an explanatory error.
 */
export default function AdminLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const u = await login(email.trim(), password);
            if (!u?.is_super_admin) {
                setError("Denne konto er ikke platform-admin. Brug den almindelige login.");
                setLoading(false);
                return;
            }
            const target = location.state?.from || "/admin/overview";
            navigate("/admin/2fa", { replace: true, state: { from: target } });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6">
                    <Logo height={56} />
                </div>
                <div className="bg-white rounded-2xl shadow-2xl border border-slate-200/60 p-7" data-testid="admin-login-card">
                    <div className="flex items-center gap-2 mb-6">
                        <ShieldCheck className="w-5 h-5 text-blue-600" />
                        <div>
                            <h1 className="text-lg font-bold text-slate-900">Platform-admin login</h1>
                            <p className="text-xs text-slate-500 mt-0.5">Kun for MyServiceHelper-ejere. 2FA påkrævet.</p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="admin-email" className="text-xs font-medium text-slate-700">Email</Label>
                            <div className="relative mt-1">
                                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input
                                    id="admin-email"
                                    data-testid="admin-login-email"
                                    type="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="platform-admin@myservicehelper.dk"
                                    required
                                    className="pl-9 h-11"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="admin-password" className="text-xs font-medium text-slate-700">Adgangskode</Label>
                            <div className="relative mt-1">
                                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input
                                    id="admin-password"
                                    data-testid="admin-login-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    className="pl-9 h-11"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-sm" data-testid="admin-login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full h-11 bg-blue-600 hover:bg-blue-700"
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Fortsæt til 2FA
                        </Button>
                    </form>

                    <div className="mt-5 pt-5 border-t border-slate-100 text-center">
                        <Link to="/login" className="text-xs text-slate-500 hover:text-slate-700" data-testid="admin-login-back-to-tenant">
                            ← Almindelig virksomhedslogin
                        </Link>
                    </div>
                </div>
                <p className="text-center text-[11px] text-slate-400 mt-4">
                    2FA-koden sendes til den registrerede admin-mail.
                </p>
            </div>
        </div>
    );
}
